import React, { useState, useCallback } from "react";
import { ChevronDown } from "react-feather";
import { RadioButton, RadioButtonOption } from "../radio-button/radio-button";
import { EvidenceCard } from "../evidence-card/evidence-card";
import "./criterion-question.scss";

export interface EvidenceCardConfig {
  title?: string;
  statements?: string[];
  cardBackGroundColor?: string;
  /** Optional element rendered at the extreme right of the title row. */
  titleAction?: React.ReactNode;
}

export interface CriterionQuestionProps {
  stepNumber?: number | string;
  title: string;
  badge?: React.ReactNode;

  radioName: string;
  radioOptions: RadioButtonOption[];
  radioValue?: string;
  onRadioChange?: (
    value: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  radioDisabled?: boolean;
  radioSelectedColor?: string;

  evidenceCards?: EvidenceCardConfig[];
  /** @deprecated Use evidenceCards instead */
  evidenceTitle?: string;
  /** @deprecated Use evidenceCards instead */
  evidenceStatements?: string[];
  /** @deprecated Use evidenceCards instead */
  evidenceCardBackGroundColor?: string;

  collapsible?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;

  /** Optional content rendered between the header row and the radio options. */
  headerSlot?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  id?: string;
}

const CriterionQuestion = ({
  stepNumber,
  title,
  badge,
  radioName,
  radioOptions,
  radioValue,
  onRadioChange,
  radioDisabled,
  radioSelectedColor,
  evidenceCards,
  evidenceTitle,
  evidenceStatements,
  evidenceCardBackGroundColor,
  collapsible = false,
  defaultExpanded = true,
  expanded: expandedProp,
  onExpandedChange,
  headerSlot,
  children,
  className,
  id,
}: CriterionQuestionProps) => {
  const isControlled = expandedProp !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = isControlled ? expandedProp : internalExpanded;

  const toggleExpanded = useCallback(() => {
    const next = !isExpanded;
    if (!isControlled) setInternalExpanded(next);
    onExpandedChange?.(next);
  }, [isExpanded, isControlled, onExpandedChange]);

  const titleId = id ? `${id}-title` : undefined;

  const resolvedEvidenceCards: EvidenceCardConfig[] =
    evidenceCards != null && evidenceCards.length > 0
      ? evidenceCards
      : evidenceTitle != null ||
          (evidenceStatements != null && evidenceStatements.length > 0)
        ? [
            {
              title: evidenceTitle,
              statements: evidenceStatements,
              cardBackGroundColor: evidenceCardBackGroundColor,
            },
          ]
        : [];

  const hasEvidence = resolvedEvidenceCards.length > 0;

  return (
    <section
      id={id}
      className={["criterion-question", className].filter(Boolean).join(" ")}
      aria-labelledby={titleId}
    >
      {stepNumber != null && (
        <div className="criterion-question__step-column">
          <span className="criterion-question__step">{stepNumber}.</span>
        </div>
      )}

      <div className="criterion-question__body">
        <div className="criterion-question__header">
          <h3 id={titleId} className="criterion-question__title">
            {title}
          </h3>
          <div className="criterion-question__header-actions">
            {badge}
            {collapsible && (
              <button
                type="button"
                className={[
                  "criterion-question__toggle",
                  isExpanded ? "" : "criterion-question__toggle--collapsed",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={toggleExpanded}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                <ChevronDown size={16} />
              </button>
            )}
          </div>
        </div>

        <div
          className={[
            "criterion-question__collapsible",
            isExpanded
              ? "criterion-question__collapsible--expanded"
              : "criterion-question__collapsible--collapsed",
          ].join(" ")}
          aria-hidden={!isExpanded}
        >
          <div className="criterion-question__collapsible-inner">
            {headerSlot != null && (
              <div className="criterion-question__header-slot">
                {headerSlot}
              </div>
            )}
            <div className="criterion-question__content">
              <RadioButton
                name={radioName}
                options={radioOptions}
                value={radioValue}
                onChange={onRadioChange}
                disabled={radioDisabled}
                selectedColor={radioSelectedColor}
              />

              {hasEvidence &&
                resolvedEvidenceCards.map((card, index) => (
                  <EvidenceCard
                    key={index}
                    title={card.title}
                    statements={card.statements}
                    cardBackGroundColor={card.cardBackGroundColor}
                    titleAction={card.titleAction}
                  />
                ))}
            </div>

            {children != null && (
              <div className="criterion-question__children">{children}</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export { CriterionQuestion };
export default CriterionQuestion;
